import { openDB, type DBSchema } from "idb"

export interface CachedDictionary {
  lang: string
  translationVersion: number | null
  fetchedAt: string
  data: Record<string, string>
}

interface LocalizationDB extends DBSchema {
  dictionaries: {
    key: string
    value: CachedDictionary
  }
}

const DB_NAME = "listenWebLocalization"
const STORE_NAME = "dictionaries"
const LEGACY_CACHE_VERSION = 1
const legacyKeyFor = (lang: string) =>
  `localization-dictionary-${lang}-v${LEGACY_CACHE_VERSION}`

const getDb = () =>
  openDB<LocalizationDB>(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    },
  })

export const localizationCache = {
  async getDictionary(lang: string): Promise<CachedDictionary | null> {
    try {
      const db = await getDb()
      const cached = await db.get(STORE_NAME, lang)
      if (cached) return cached

      const legacyValue = localStorage.getItem(legacyKeyFor(lang))
      if (!legacyValue) return null
      const legacy = JSON.parse(legacyValue) as {
        version: number
        data: Record<string, string>
      }
      const migrated: CachedDictionary = {
        lang,
        translationVersion: null,
        fetchedAt: new Date().toISOString(),
        data: legacy.data,
      }
      await db.put(STORE_NAME, migrated, lang)
      localStorage.removeItem(legacyKeyFor(lang))
      return migrated
    } catch {
      return null
    }
  },

  async saveDictionary(
    lang: string,
    data: Record<string, string>,
    translationVersion: number
  ): Promise<void> {
    const db = await getDb()
    await db.put(
      STORE_NAME,
      {
        lang,
        translationVersion,
        fetchedAt: new Date().toISOString(),
        data,
      },
      lang
    )
  },
}
